import { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { SwiperSlide } from "swiper/react";

import ArticleCard from "@components/cards/ArticleCard";
import Dot from "@components/Dot";
import styles from "@components/explore/Explore.module.css";
import { TagProps } from "@components/labels/Tag";
import CardSlides from "@components/layout/CardSlides";
import BodyCopy from "@components/typography/BodyCopy";
import Heading2 from "@components/typography/Heading2";
import { ExploreSectionsEnum } from "@typing/Enums";
import { FocusAreaEnum, GetArticlesBasicQuery, GetRecommendedArticlesQuery } from "@typing/Generated";
import { imageUrl } from "@utils/imageUtils";
import { groupArticlesByFocusArea, tagPropsForFocusArea } from "@utils/modelUtils";

type Article = NonNullable<NonNullable<NonNullable<GetArticlesBasicQuery["articles"]["edges"]>[number]>["node"]>;

type Props = {
  allArticles: Article[];
  exploreSection: ExploreSectionsEnum;
  fetchMore: () => void;
  journeyId: string;
  recommendedArticles: GetRecommendedArticlesQuery["recommendedArticles"];
};

type SectionProps = {
  articles: Article[];
  fetchMore: () => void;
  focusArea?: FocusAreaEnum;
  journeyId: string;
  tagProps?: TagProps;
  titleKey?: string;
};

const Section = ({ articles, fetchMore, focusArea, journeyId, tagProps, titleKey }: SectionProps) => {
  if (articles.length === 0) return null;

  return (
    <div className={`${styles.articleSection} ${titleKey ? styles.focusAreaSpacing : ""}`}>
      {titleKey && tagProps && (
        <div className={styles.articlesHeader}>
          <div className={styles.dotSpacing}>
            <Dot color={tagProps?.color} size="small" unread />
          </div>
          <Heading2>
            <FormattedMessage id={titleKey} />
          </Heading2>
        </div>
      )}

      <CardSlides itemCount={articles.length} onSlideNextTransitionStart={fetchMore}>
        {!titleKey && (
          <SwiperSlide>
            <div className={styles.expertRecommendsCardContainer}>
              <div className={styles.expertRecommendsCard}>
                <div className={styles.fullHeight}>
                  <img className={styles.expertRecommendsImg} src={imageUrl("computerHeart")} />
                </div>
                <div className={styles.hideMobile}>
                  <Heading2 color="white">
                    <FormattedMessage id="pages.explore.articles.expertRecommended" />
                  </Heading2>
                </div>
                <div className={styles.showMobile}>
                  <BodyCopy bold color="white">
                    <FormattedMessage id="pages.explore.articles.expertRecommended" />
                  </BodyCopy>
                </div>
              </div>
            </div>
          </SwiperSlide>
        )}
        {articles.map(article => (
          <SwiperSlide key={article.id}>
            <ArticleCard article={article} focusArea={focusArea} journeyId={journeyId} />
          </SwiperSlide>
        ))}
      </CardSlides>
    </div>
  );
};

const Articles = ({ allArticles, exploreSection, fetchMore, journeyId, recommendedArticles }: Props) => {
  const articlesGrouped = useMemo(() => groupArticlesByFocusArea(allArticles), [allArticles]);
  const intl = useIntl();
  const keyOrder: FocusAreaEnum[] = [
    FocusAreaEnum.WELLBEING,
    FocusAreaEnum.MEDICAL,
    FocusAreaEnum.LEGAL,
    FocusAreaEnum.FUNCTIONAL,
    FocusAreaEnum.FINANCIAL,
    FocusAreaEnum.SOCIAL,
    FocusAreaEnum.UNDER_EIGHTEEN
  ];

  if (exploreSection !== ExploreSectionsEnum.ARTICLES && exploreSection !== ExploreSectionsEnum.EVENTS) return null;

  return (
    <div>
      <Section articles={recommendedArticles} fetchMore={fetchMore} journeyId={journeyId} />
      {keyOrder.map(key => {
        const articles = articlesGrouped[key];
        return (
          <Section
            articles={articles}
            fetchMore={fetchMore}
            focusArea={key}
            journeyId={journeyId}
            key={key}
            tagProps={tagPropsForFocusArea(key, intl)}
            titleKey={`models.focusArea.${key}`}
          />
        );
      })}

      <Section
        articles={allArticles.filter(a => a.webinarCompleted)}
        fetchMore={fetchMore}
        journeyId={journeyId}
        tagProps={{ color: "success-500", text: "" }}
        titleKey={"pages.explore.events.title"}
      />
    </div>
  );
};

export default Articles;
